import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import * as d3 from 'd3';
import sankeyData from '../data/sankeyDataset.json';

const Wrapper = styled.div`
  width: 100%;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  padding: 14px;
  overflow: hidden;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 14px;
  margin-bottom: 12px;
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  strong {
    font-size: 1rem;
    letter-spacing: -0.01em;
  }

  span {
    color: var(--muted);
    font-size: 0.95rem;
    line-height: 1.4;
    max-width: 70ch;
  }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
`;

const Segmented = styled.div`
  display: inline-flex;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.65);
  padding: 4px;
  gap: 4px;
`;

const SegButton = styled.button`
  border: 0;
  border-radius: 999px;
  padding: 8px 10px;
  cursor: pointer;
  font-weight: 750;
  font-size: 0.95rem;
  background: ${p => (p.$active ? 'var(--primary)' : 'transparent')};
  color: ${p => (p.$active ? 'white' : 'var(--muted)')};

  &:hover {
    color: ${p => (p.$active ? 'white' : 'var(--text)')};
  }

  &:focus-visible {
    outline: 3px solid rgba(38, 179, 168, 0.45);
    outline-offset: 3px;
  }
`;

const Select = styled.select`
  border-radius: 999px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.65);
  padding: 8px 10px;
  color: var(--text);
  font-weight: 700;

  &:focus-visible {
    outline: 3px solid rgba(38, 179, 168, 0.45);
    outline-offset: 3px;
  }
`;

const VizArea = styled.div`
  position: relative;
  width: 100%;
  min-height: 420px;
`;

const Svg = styled.svg`
  width: 100%;
  height: 100%;
  display: block;
`;

const Tooltip = styled.div`
  position: absolute;
  pointer-events: none;
  opacity: ${p => (p.$visible ? 1 : 0)};
  transform: translate(-50%, calc(-100% - 12px));
  background: rgba(12, 17, 20, 0.92);
  color: white;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 0.9rem;
  line-height: 1.35;
  max-width: 320px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.10);

  strong {
    display: block;
    margin-bottom: 4px;
  }

  span {
    color: rgba(255, 255, 255, 0.8);
  }
`;

function useResizeObserver(ref) {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!ref.current) return;

    const el = ref.current;
    const ro = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const cr = entry.contentRect;
      setSize({ width: cr.width, height: cr.height });
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return size;
}

function formatNumberPT(n) {
  // Keep it simple: spaces as thousand separators
  return new Intl.NumberFormat('pt-PT').format(n);
}

function computeDerived(data) {
  const links = data.links;

  const districts = Array.from(new Set(links.map(l => l.source))).sort((a, b) => a.localeCompare(b));
  const parties = Array.from(new Set(links.map(l => l.target))).sort((a, b) => a.localeCompare(b));

  const votesByDistrictParty = new Map();
  const districtTotals = new Map();
  const partyTotals = new Map();

  for (const l of links) {
    const key = `${l.source}__${l.target}`;
    votesByDistrictParty.set(key, (votesByDistrictParty.get(key) || 0) + l.value);
    districtTotals.set(l.source, (districtTotals.get(l.source) || 0) + l.value);
    partyTotals.set(l.target, (partyTotals.get(l.target) || 0) + l.value);
  }

  const partiesByNationalVotes = [...partyTotals.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([id]) => id);

  // A simple “concentration” metric per district (Herfindahl-Hirschman Index)
  // Range: ~1/numParties (spread) to 1 (all votes in one party)
  const concentration = districts.map(d => {
    const total = districtTotals.get(d) || 0;
    let hhi = 0;
    for (const p of parties) {
      const v = votesByDistrictParty.get(`${d}__${p}`) || 0;
      const s = total > 0 ? v / total : 0;
      hhi += s * s;
    }
    return { district: d, hhi, total };
  });

  return {
    districts,
    parties,
    votesByDistrictParty,
    districtTotals,
    partyTotals,
    partiesByNationalVotes,
    concentration,
  };
}

function Heatmap({ width, height, derived, topN, onHover }) {
  const margin = { top: 24, right: 12, bottom: 92, left: 120 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);

  const parties = derived.partiesByNationalVotes.slice(0, topN);
  const districts = derived.districts;

  const values = [];
  for (const d of districts) {
    for (const p of parties) {
      values.push(derived.votesByDistrictParty.get(`${d}__${p}`) || 0);
    }
  }

  const maxV = d3.max(values) || 1;
  const x = d3.scaleBand().domain(parties).range([0, innerW]).padding(0.08);
  const y = d3.scaleBand().domain(districts).range([0, innerH]).padding(0.06);

  const color = d3
    .scaleSequential()
    .domain([0, Math.sqrt(maxV)])
    .interpolator(t => d3.interpolateRgb('#eef6f5', '#0f2d2c')(t));

  const cells = [];
  for (const d of districts) {
    for (const p of parties) {
      const v = derived.votesByDistrictParty.get(`${d}__${p}`) || 0;
      const total = derived.districtTotals.get(d) || 0;
      const share = total > 0 ? v / total : 0;

      cells.push({
        district: d,
        party: p,
        value: v,
        share,
        x: x(p),
        y: y(d),
        w: x.bandwidth(),
        h: y.bandwidth(),
        fill: color(Math.sqrt(v)),
      });
    }
  }

  // grid lines (subtle)
  const gridLinesX = parties.map(p => x(p));

  return (
    <Svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Mapa de calor: votos por círculo e partido">
      <g transform={`translate(${margin.left},${margin.top})`}>
        {/* axes labels */}
        <text x={0} y={-10} fill="rgba(12,17,20,0.66)" fontSize={12} fontWeight={650}>
          Círculo eleitoral →
        </text>

        {/* cells */}
        <g>
          {cells.map(c => (
            <rect
              key={`${c.district}__${c.party}`}
              x={c.x}
              y={c.y}
              width={c.w}
              height={c.h}
              rx={8}
              fill={c.fill}
              stroke="rgba(12,17,20,0.06)"
              onMouseEnter={e => onHover(e, c)}
              onMouseMove={e => onHover(e, c)}
              onMouseLeave={() => onHover(null, null)}
            />
          ))}
        </g>

        {/* y labels */}
        <g>
          {districts.map(d => (
            <text
              key={d}
              x={-10}
              y={(y(d) || 0) + y.bandwidth() / 2}
              textAnchor="end"
              dominantBaseline="middle"
              fill="rgba(12,17,20,0.75)"
              fontSize={12}
              fontWeight={650}
            >
              {d}
            </text>
          ))}
        </g>

        {/* x labels */}
        <g transform={`translate(0, ${innerH})`}>
          {parties.map(p => (
            <g key={p} transform={`translate(${(x(p) || 0) + x.bandwidth() / 2},0)`}>
              <text
                y={14}
                transform="rotate(45)"
                textAnchor="start"
                fill="rgba(12,17,20,0.75)"
                fontSize={12}
                fontWeight={750}
              >
                {p}
              </text>
            </g>
          ))}
        </g>

        {/* subtle grid lines */}
        <g>
          {gridLinesX.map((gx, i) => (
            <line
              key={i}
              x1={gx}
              x2={gx}
              y1={0}
              y2={innerH}
              stroke="rgba(12,17,20,0.03)"
            />
          ))}
        </g>

        {/* legend */}
        <g transform={`translate(${Math.max(0, innerW - 220)}, ${-8})`}>
          <rect x={0} y={0} width={220} height={22} rx={11} fill="rgba(255,255,255,0.55)" stroke="rgba(12,17,20,0.08)" />
          <text x={10} y={15} fontSize={12} fill="rgba(12,17,20,0.66)" fontWeight={650}>
            Cor = mais votos
          </text>
          <circle cx={168} cy={11} r={5} fill={color(0)} stroke="rgba(12,17,20,0.08)" />
          <circle cx={186} cy={11} r={5} fill={color(Math.sqrt(maxV * 0.25))} stroke="rgba(12,17,20,0.08)" />
          <circle cx={204} cy={11} r={5} fill={color(Math.sqrt(maxV))} stroke="rgba(12,17,20,0.08)" />
        </g>
      </g>
    </Svg>
  );
}

function ConcentrationBars({ width, height, derived, onHover }) {
  const margin = { top: 16, right: 16, bottom: 28, left: 170 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);

  const sorted = [...derived.concentration].sort((a, b) => b.hhi - a.hhi);
  const top = sorted.slice(0, 12);

  const x = d3.scaleLinear().domain([0, d3.max(top, d => d.hhi) || 1]).range([0, innerW]);
  const y = d3.scaleBand().domain(top.map(d => d.district)).range([0, innerH]).padding(0.22);

  return (
    <Svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Barras: concentração de votos por círculo">
      <g transform={`translate(${margin.left},${margin.top})`}>
        <text x={0} y={-4} fill="rgba(12,17,20,0.66)" fontSize={12} fontWeight={650}>
          Concentração (HHI) — maior = votos mais concentrados
        </text>

        {top.map(d => {
          const barW = x(d.hhi);
          const barY = y(d.district) || 0;
          return (
            <g key={d.district} transform={`translate(0, ${barY})`}>
              <rect
                x={0}
                y={0}
                width={barW}
                height={y.bandwidth()}
                rx={10}
                fill="rgba(38, 179, 168, 0.28)"
                stroke="rgba(38, 179, 168, 0.35)"
                onMouseEnter={e => onHover(e, { ...d, kind: 'hhi' })}
                onMouseMove={e => onHover(e, { ...d, kind: 'hhi' })}
                onMouseLeave={() => onHover(null, null)}
              />
              <text
                x={-12}
                y={y.bandwidth() / 2}
                textAnchor="end"
                dominantBaseline="middle"
                fill="rgba(12,17,20,0.75)"
                fontSize={12}
                fontWeight={650}
              >
                {d.district}
              </text>
              <text
                x={Math.min(barW + 8, innerW - 10)}
                y={y.bandwidth() / 2}
                dominantBaseline="middle"
                fill="rgba(12,17,20,0.72)"
                fontSize={12}
                fontWeight={750}
              >
                {d.hhi.toFixed(3)}
              </text>
            </g>
          );
        })}

        <line x1={0} x2={innerW} y1={innerH + 8} y2={innerH + 8} stroke="rgba(12,17,20,0.08)" />
      </g>
    </Svg>
  );
}

function SankeyDiagram() {
  const containerRef = useRef(null);
  const { width, height } = useResizeObserver(containerRef);

  const derived = useMemo(() => computeDerived(sankeyData), []);

  const [view, setView] = useState('heatmap');
  const [topN, setTopN] = useState(8);

  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, title: '', body: '' });

  const handleHover = (evt, datum) => {
    if (!evt || !datum) {
      setTooltip(t => ({ ...t, visible: false }));
      return;
    }

    const rect = containerRef.current?.getBoundingClientRect();
    const x = evt.clientX - (rect?.left || 0);
    const y = evt.clientY - (rect?.top || 0);

    if (datum.kind === 'hhi') {
      setTooltip({
        visible: true,
        x,
        y,
        title: datum.district,
        body: `Concentração (HHI): ${datum.hhi.toFixed(3)}\nTotal de votos (dataset): ${formatNumberPT(datum.total)}`,
      });
      return;
    }

    setTooltip({
      visible: true,
      x,
      y,
      title: `${datum.district} → ${datum.party}`,
      body: `${formatNumberPT(datum.value)} votos\n${(datum.share * 100).toFixed(1)}% dentro do círculo`,
    });
  };

  return (
    <Wrapper>
      <HeaderRow>
        <Title>
          <strong>Explorar padrões (sem “teia” visual)</strong>
          <span>
            Em vez de mostrar centenas de ligações ao mesmo tempo, esta visualização deixa-te ver rapidamente <em>onde</em> há mais votos e
            <em> quão concentrados</em> estão por círculo.
          </span>
        </Title>

        <Controls>
          <Segmented aria-label="Escolher tipo de visualização">
            <SegButton type="button" $active={view === 'heatmap'} onClick={() => setView('heatmap')}>Mapa</SegButton>
            <SegButton type="button" $active={view === 'concentration'} onClick={() => setView('concentration')}>Concentração</SegButton>
          </Segmented>

          {view === 'heatmap' && (
            <Select value={topN} onChange={e => setTopN(Number(e.target.value))} aria-label="Número de partidos a mostrar">
              <option value={5}>Top 5 partidos</option>
              <option value={8}>Top 8 partidos</option>
              <option value={12}>Top 12 partidos</option>
            </Select>
          )}
        </Controls>
      </HeaderRow>

      <VizArea ref={containerRef}>
        {width > 0 && height > 0 && view === 'heatmap' && (
          <Heatmap width={width} height={Math.max(460, height)} derived={derived} topN={topN} onHover={handleHover} />
        )}

        {width > 0 && height > 0 && view === 'concentration' && (
          <ConcentrationBars width={width} height={Math.max(420, height)} derived={derived} onHover={handleHover} />
        )}

        <Tooltip $visible={tooltip.visible} style={{ left: tooltip.x, top: tooltip.y }}>
          <strong>{tooltip.title}</strong>
          <span style={{ whiteSpace: 'pre-line' }}>{tooltip.body}</span>
        </Tooltip>
      </VizArea>
    </Wrapper>
  );
}

export default SankeyDiagram;
